import { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query';
import { APP_ENDPOINTS } from 'app/endpoints';
import { ResponseCompanyDto } from 'features/company/types/dtos';
import { BaseQueryParams } from 'types/base-query-params';
import { PaginatedResponse } from 'types/dtos';
import { handleDuplicates } from 'utils/array-helpers';
import { constructQueryParams } from 'utils/query-params-builder';
import { ResponseWebsiteDto } from '../types/dtos';
import { store } from 'store/index';
import { APP_ROLES_NAMES } from 'constants/app-roles';
import { checkError, fetchResponse } from 'utils/api-helpers';
import { formatDate } from 'utils/date-helpers';

export type WebsitesWithJoinQueryParams = BaseQueryParams & {
    name?: string;
    domain?: string;
    companyId?: string;
    createdAt?: string;
};

export default (build: EndpointBuilder<BaseQueryFn, string, string>) =>
    build.query<PaginatedResponse<ResponseWebsiteDto>, WebsitesWithJoinQueryParams>({
        async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
            const role = store.getState().auth.role;
            const isAdmin = role === APP_ROLES_NAMES.ADMINISTRATOR;

            const websitesResponse = await fetchWithBQ(constructQueryParams(APP_ENDPOINTS.WEBSITES, _arg));
            if (websitesResponse.error) {
                return { error: websitesResponse.error };
            }
            const websites = websitesResponse.data as PaginatedResponse<ResponseWebsiteDto>;

            let companies: PaginatedResponse<ResponseCompanyDto>;
            if (isAdmin) {
                const companyIds = handleDuplicates(websites.content.map((website) => website.companyId));
                const companiesResponse = await fetchResponse(APP_ENDPOINTS.COMPANIES, companyIds, fetchWithBQ);
                const companyError = checkError(companiesResponse);
                if (companyError) return companyError;
                companies = companiesResponse.data as PaginatedResponse<ResponseCompanyDto>;
            }

            websites.content = websites.content.map((website) => {
                const companyName = isAdmin
                    ? companies.content.find((company) => {
                          return company.id === website.companyId;
                      })?.name
                    : undefined;

                const createdAt = formatDate(website.createdAt);

                return {
                    ...website,
                    createdAt,
                    companyName,
                };
            });

            return {
                data: websites,
            };
        },
        providesTags: [{ type: 'websites-with-join', id: 'GET-JOIN' }],
    });
